import { useEffect, useRef, type RefObject } from 'react';

/**
 * Bedienbare Elemente innerhalb eines Bereichs (für Fokusfalle und ersten Fokus): nur sichtbare und bedienbare –
 * nicht in versteckten Reitern (`hidden`), nicht mit `display: none`/`visibility: hidden`, nicht unter `inert` oder
 * `aria-hidden`, nicht gesperrt, nicht mit `tabindex="-1"`.
 */
export function focusables(root: HTMLElement): HTMLElement[] {
  return [
    ...root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [contenteditable="true"], [tabindex]:not([tabindex="-1"])',
    ),
  ].filter(
    (el) =>
      // Nicht per Tab erreichbar (z. B. inaktive Reiter mit wanderndem Tab-Stopp).
      el.getAttribute('tabindex') !== '-1' &&
      !el.closest('[hidden], [inert], [aria-hidden="true"], fieldset:disabled') &&
      el.getClientRects().length > 0 &&
      getComputedStyle(el).visibility !== 'hidden',
  );
}

/** Fokus steht in einem Eingabefeld (Esc und Kürzel gehören dann dem Feld). */
export function isFieldTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
}

/** Rollen von Bedienelementen, die ↑/↓ selbst nutzen (Menü, Liste, Auswahl, Reiter, Leiste, Regler, Baum, Raster). */
const ARROW_OWNER_SELECTOR = ['menu', 'menubar', 'menuitem', 'listbox', 'option', 'radiogroup', 'radio', 'tablist', 'tab', 'toolbar', 'slider', 'spinbutton', 'combobox', 'tree', 'treegrid', 'grid']
  .map((role) => `[role=${role}]`)
  .join(',');

/**
 * Gehören ↑/↓ dem Ziel selbst? Felder (Eingabe, Text, Auswahl, editierbar) und Bedienelemente mit eigenen Pfeiltasten
 * (Ticket 1.272: Seitenpanel und Listenzeilen nehmen ihnen die Pfeile nie weg).
 */
export function ownsArrowKeys(target: EventTarget | null): boolean {
  return isFieldTarget(target) || (target instanceof Element && target.closest(ARROW_OWNER_SELECTOR) !== null);
}

// ---------------------------------------------------------------------------
// Ebenen-Stapel (Story 1.23): Jede offene Überlagerung (Seitenpanel, Blatt, Schublade, Suche am Handy, Popover, Glocke,
// Seitenleiste als Überlagerung) legt sich beim Öffnen oben auf den Stapel und nimmt sich beim Schließen wieder herunter.
// Genau EIN Tastatur-Lauscher am Dokument verteilt Esc, Tab (Fokusfalle) und Kürzel nur an die oberste Ebene; tiefere
// Ebenen und die Seite bekommen nichts, solange eine höhere offen ist. Schließen gibt Fokus und Steuerung an die
// darunterliegende Ebene zurück.

export interface LayerOptions {
  /** Bereich der Ebene (Fokusfalle, „gehört zur obersten Ebene“). */
  root: RefObject<HTMLElement | null>;
  /** Fokusfalle: Tab/Shift+Tab bleiben in der Ebene (modale Bereiche). */
  trap?: boolean;
  /** Esc schließt die Ebene; ohne Angabe tut Esc nichts. */
  onEscape?: () => void;
  /** `false`: Esc schließt nicht, solange der Fokus in einem Feld steht (ungespeicherte Eingaben bleiben). */
  escapeInFields?: boolean;
}

interface Layer {
  options: { current: LayerOptions };
}

const stack: Layer[] = [];

function trapTab(e: KeyboardEvent, root: HTMLElement) {
  const items = focusables(root);
  if (items.length === 0) {
    e.preventDefault();
    return;
  }
  const firstItem = items[0]!;
  const lastItem = items[items.length - 1]!;
  const active = document.activeElement as HTMLElement | null;
  // Fokus auf dem Bereich selbst oder einem Element außerhalb der Tab-Reihenfolge (z. B. Überschrift mit tabindex=-1
  // beim Öffnen) oder außerhalb der Ebene: Tab geht zum ersten, Shift+Tab zum letzten Element.
  const outside = !active || !items.includes(active);
  if (e.shiftKey && (active === firstItem || outside)) {
    e.preventDefault();
    lastItem.focus();
  } else if (!e.shiftKey && (active === lastItem || outside)) {
    e.preventDefault();
    firstItem.focus();
  }
}

// Bubble-Phase am Dokument: Tastatur-Handler der Bausteine (React) laufen vorher und können Esc mit `preventDefault`
// für sich nehmen (z. B. E-Mail-Entwurf, Auswahl-Liste der @Erwähnung).
function onDocumentKey(e: KeyboardEvent) {
  const top = stack[stack.length - 1];
  if (!top) return;
  const o = top.options.current;
  if (e.key === 'Escape') {
    if (e.defaultPrevented || !o.onEscape) return;
    if (o.escapeInFields === false && isFieldTarget(e.target)) return;
    e.preventDefault();
    o.onEscape();
    return;
  }
  if (e.key === 'Tab') {
    // Fokusfalle der obersten modalen Ebene (ein nicht modales Popover im Seitenpanel lässt den Fokus im Panel).
    const modal = [...stack].reverse().find((l) => l.options.current.trap);
    const root = modal?.options.current.root.current;
    if (root) trapTab(e, root);
  }
}

/** Eine Ebene auf den Stapel legen, solange `active` gilt. */
export function useLayer(active: boolean, options: LayerOptions): void {
  const ref = useRef(options);
  useEffect(() => {
    ref.current = options;
  });
  useEffect(() => {
    if (!active) return;
    const layer: Layer = { options: ref };
    if (stack.length === 0) document.addEventListener('keydown', onDocumentKey);
    stack.push(layer);
    return () => {
      const i = stack.indexOf(layer);
      if (i >= 0) stack.splice(i, 1);
      if (stack.length === 0) document.removeEventListener('keydown', onDocumentKey);
    };
  }, [active]);
}

// Scrollsperre der Seite zentral (Story 1.23, Zweit-Review): gesperrt, solange irgendeine modale Ebene offen ist. Den
// Ursprungszustand merkt sich die erste Sperre; erst die letzte Freigabe stellt ihn her – unabhängig davon, in welcher
// Reihenfolge Ebenen ausgehängt werden (z. B. Panel und Blatt gemeinsam bei einer Navigation).
let scrollLocks = 0;
let savedOverflow = '';

function lockScroll(): void {
  if (scrollLocks === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLocks += 1;
}

function unlockScroll(): void {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) document.body.style.overflow = savedOverflow;
}

/** Ist eine Überlagerung offen? */
export function hasOpenLayer(): boolean {
  return stack.length > 0;
}

/**
 * Gehört `el` zur obersten Ebene (oder ist keine offen)? Tasten von Bausteinen außerhalb einer Ebene (z. B. Esc am
 * Tooltip) wirken nur dann.
 */
export function isInTopLayer(el: Element | null): boolean {
  const top = stack[stack.length - 1];
  if (!top) return true;
  const root = top.options.current.root.current;
  return !!el && !!root && root.contains(el);
}

/**
 * Modaler Bereich (Schublade, Suche am Handy, Blatt von unten, Seitenpanel): eigene Ebene mit Fokusfalle; Fokus beim
 * Öffnen hinein, Esc schließt (nur die oberste Ebene); beim Schließen kehrt der Fokus auf den auslösenden Knopf zurück.
 * Die Seite dahinter scrollt nicht mit. Mit `escapeInFields: false` schließt Esc nicht, solange der Fokus in einem Feld
 * steht.
 */
export function useModalFocus(
  panel: RefObject<HTMLElement | null>,
  opener: RefObject<HTMLElement | null>,
  onClose: () => void,
  initial?: () => HTMLElement | null,
  options: { escapeInFields?: boolean } = {},
) {
  const close = useRef(onClose);
  const first = useRef(initial);
  useEffect(() => {
    close.current = onClose;
    first.current = initial;
  });
  useLayer(true, {
    root: panel,
    trap: true,
    onEscape: () => close.current(),
    ...(options.escapeInFields === undefined ? {} : { escapeInFields: options.escapeInFields }),
  });
  useEffect(() => {
    const root = panel.current;
    if (!root) return;
    const returnTo = opener.current;
    (first.current?.() ?? focusables(root)[0] ?? root).focus();
    lockScroll();
    return () => {
      unlockScroll();
      // Nur auf einen noch vorhandenen Knopf zurück (nach einer Navigation ist er weg) und nicht, wenn eine andere Ebene
      // den Fokus inzwischen hält.
      if (returnTo?.isConnected && (!hasOpenLayer() || isInTopLayer(returnTo))) returnTo.focus();
    };
  }, [panel, opener]);
}
